import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  travelCardClaimBody,
  TravellerWithCollectionMembershipsDTO,
  TravelCardResponseDTO,
  getGetTravellerBearersQueryKey,
  useGetTravellerBearers,
  useGetTicketsForTraveller,
  getGetTicketsForTravellerQueryKey,
  getGetTicketsForUserOrganizationQueryKey,
  useUpdateBearerForIssuedTicket,
  getGetBearersForIssuedTicketQueryKey,
  useGetBearersForIssuedTicketHook,
  useLendIssuedTicket,
  useTravelCardClaim,
  useTravelCardIsClaimable,
} from '@/api/trafikportalen';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import Input from '../core/Input';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { IconCheck } from '@/icons/CoreIcons';
import Button from '../core/Button';
import { Spinner } from '../core/Spinner';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { InputText } from '../Inputs/InputText';
import { getApiErrorMessage } from '@/helpers/apiError';
import { getClaimabilityError } from '@/helpers/claimability';
import { blankToUndefined, trimData } from '@/helpers/trimData';
import { useFavoredBearer } from '@/hooks/useFavoredBearer';

const cardFormSchema = travelCardClaimBody.omit({ traveller_id: true });

type CardFormType = z.infer<typeof cardFormSchema>;

interface TravelCardCreateFormProps {
  onOpenChange: (open: boolean) => void;
  onSuccess: (newCard: TravelCardResponseDTO) => void;
  traveller: TravellerWithCollectionMembershipsDTO;
}

export const TravelCardCreateForm = ({
  onOpenChange,
  traveller,
  onSuccess,
}: TravelCardCreateFormProps) => {
  const customErrorMap = useCustomErrorMap();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const { data: travellerBearers } = useGetTravellerBearers(
    traveller.traveller.id,
  );

  const cardResolver = zodResolver(cardFormSchema, {
    errorMap: customErrorMap,
  });

  const travelCardClaim = useTravelCardClaim();
  const moveTicket = useUpdateBearerForIssuedTicket();
  const lendTicket = useLendIssuedTicket();
  const getBearersForIssuedTicket = useGetBearersForIssuedTicketHook();
  const { setFavoredBearer, getFavoredBearerErrorMessage } = useFavoredBearer({
    travellerId: traveller.traveller.id,
  });

  const { data: travellerTickets, isLoading: isLoadingTickets } =
    useGetTicketsForTraveller(traveller.traveller.id);

  const ticketsToMove = useMemo(
    () =>
      (travellerTickets ?? []).filter(
        (ticket) => ticket.state === 'active' || ticket.state === 'inactive',
      ),
    [travellerTickets],
  );

  const [isReassignFlow, setIsReassignFlow] = useState(false);
  const canManageCards = Boolean(traveller.traveller.bobcat_alltix_id);

  const cardFormMethods = useForm<CardFormType>({
    defaultValues: {
      card_serial: '',
      label: '',
    },
    resolver: cardResolver,
    mode: 'onChange',
  });

  const [formattedCardSerial, setFormattedCardSerial] = useState('');
  const [cardSerial, setCardSerial] = useState<string | null>(null);
  const [isCardValid, setIsCardValid] = useState(false);

  const cardAlreadyExistsOnTraveller = useMemo(() => {
    if (!cardSerial || cardSerial.length !== 16) {
      return false;
    }

    const allBearers = [
      ...(travellerBearers?.alltix_bearers ?? []),
      ...(travellerBearers?.private_bearers ?? []),
    ];

    return allBearers.some((bearer) => bearer.serial === cardSerial);
  }, [travellerBearers, cardSerial]);

  const {
    data: cardValidation,
    error: cardValidationError,
    isLoading: isLoadingCardValidation,
  } = useTravelCardIsClaimable(
    { serial: cardSerial ?? '' },
    {
      query: {
        enabled: canManageCards && !!cardSerial && cardSerial.length === 16,
      },
    },
  );

  const handleCardSerialChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value;

      if (value !== '0') {
        value = value.replace(/\D/g, '');
      }

      value = value.slice(0, 16);

      const formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');

      setFormattedCardSerial(formattedValue);
      setCardSerial(value);

      cardFormMethods.setValue('card_serial', value);
    },
    [cardFormMethods],
  );

  const cancelEdit = useCallback(() => {
    cardFormMethods.reset();
    setIsCardValid(false);
    onOpenChange?.(false);
  }, [onOpenChange, cardFormMethods]);

  const claimCard = useCallback(
    async (data: CardFormType) => {
      return travelCardClaim.mutateAsync({
        data: {
          traveller_id: traveller.traveller.id,
          card_serial: data.card_serial,
          label: blankToUndefined(data.label),
        },
      });
    },
    [travelCardClaim, traveller.traveller.id],
  );

  const onSubmit: SubmitHandler<CardFormType> = useCallback(
    async (data) => {
      try {
        const newCard = await claimCard(trimData(data));
        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Kortet är tillagt på resenären.',
            }),
          },
          { autoClose: 3000 },
        );

        await queryClient.invalidateQueries({
          queryKey: getGetTravellerBearersQueryKey(traveller.traveller.id),
        });

        onSuccess(newCard);
        cardFormMethods.reset();
      } catch (error) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: getApiErrorMessage(
              error,
              formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            ),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      claimCard,
      traveller.traveller.id,
      formatMessage,
      cardFormMethods,
      queryClient,
      onSuccess,
    ],
  );

  const handleAddCardAndReassignActiveTickets = useCallback(() => {
    if (ticketsToMove.length === 0) {
      return;
    }

    void cardFormMethods.handleSubmit(async (data) => {
      setIsReassignFlow(true);
      try {
        const newCard = await claimCard(trimData(data));
        const bearerId = newCard.bearer_id;

        let failed = 0;
        for (const ticket of ticketsToMove) {
          try {
            const bearers = await queryClient.fetchQuery({
              queryKey: getGetBearersForIssuedTicketQueryKey(ticket.id),
              queryFn: ({ signal }) =>
                getBearersForIssuedTicket(ticket.id, signal),
            });
            const currentBearerId = bearers?.[0]?.mtb_bearer_id;
            if (currentBearerId === bearerId) {
              continue;
            }
            const mutation = ticket.loan_transaction_id
              ? lendTicket
              : moveTicket;
            await mutation.mutateAsync({
              ticketID: ticket.id,
              data: { mtb_bearer_id: bearerId },
            });
          } catch (error) {
            console.error(
              'Failed to update bearer for ticket',
              ticket.id,
              error,
            );
            failed += 1;
          }
        }

        if (failed < ticketsToMove.length) {
          try {
            await setFavoredBearer(bearerId, { showToast: false });
          } catch (error) {
            toast.error(
              {
                title: formatMessage({
                  defaultMessage: 'Kunde inte uppdatera föredragen bärare',
                }),
                description: getFavoredBearerErrorMessage(error),
              },
              { autoClose: 3000 },
            );
          }
        }

        await queryClient.invalidateQueries({
          queryKey: getGetTravellerBearersQueryKey(traveller.traveller.id),
        });
        await queryClient.invalidateQueries({
          queryKey: getGetTicketsForTravellerQueryKey(traveller.traveller.id),
        });
        await queryClient.invalidateQueries({
          queryKey: getGetTicketsForUserOrganizationQueryKey(),
        });

        if (failed === 0) {
          toast.success(
            {
              title: formatMessage({
                defaultMessage:
                  'Kortet är tillagt och aktiva biljetter flyttades till kortet.',
              }),
            },
            { autoClose: 3000 },
          );
        } else if (failed < ticketsToMove.length) {
          toast.warning(
            {
              title: formatMessage({
                defaultMessage:
                  'Kortet tillades men vissa biljetter kunde inte flyttas.',
              }),
            },
            { autoClose: 5000 },
          );
        } else {
          toast.warning(
            {
              title: formatMessage({
                defaultMessage:
                  'Kortet tillades men biljetterna kunde inte flyttas.',
              }),
            },
            { autoClose: 5000 },
          );
        }

        onSuccess(newCard);
        cardFormMethods.reset();
      } catch (error) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: getApiErrorMessage(
              error,
              formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            ),
          },
          { autoClose: 3000 },
        );
      } finally {
        setIsReassignFlow(false);
      }
    })();
  }, [
    cardFormMethods,
    claimCard,
    traveller.traveller.id,
    formatMessage,
    queryClient,
    ticketsToMove,
    getBearersForIssuedTicket,
    lendTicket,
    moveTicket,
    onSuccess,
    setFavoredBearer,
    getFavoredBearerErrorMessage,
  ]);

  const cardError = useMemo(() => {
    if (!canManageCards) {
      return formatMessage({
        defaultMessage:
          'Kort kan inte hanteras ännu. Resenären är inte klar för korthantering.',
      });
    }
    if (cardValidationError) {
      return getApiErrorMessage(
        cardValidationError,
        formatMessage({ defaultMessage: 'Något gick fel.' }),
      );
    }
    if (!cardValidation) {
      return '';
    }
    if (!cardValidation.claimable) {
      return getClaimabilityError(cardValidation.reason, formatMessage);
    }
    return '';
  }, [canManageCards, cardValidation, cardValidationError, formatMessage]);

  useEffect(() => {
    if (isLoadingCardValidation) return;

    if (!cardSerial || cardSerial.length !== 16) {
      return;
    }

    if (cardError) {
      setIsCardValid(false);
      return;
    }

    cardFormMethods.setValue('card_serial', cardSerial, {
      shouldValidate: true,
    });
    setIsCardValid(true);
  }, [isLoadingCardValidation, cardSerial, cardFormMethods, cardError]);

  const disabled =
    !canManageCards ||
    !isCardValid ||
    isReassignFlow ||
    travelCardClaim.isPending;

  return (
    <FormProvider {...cardFormMethods}>
      <form onSubmit={cardFormMethods.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <FormSection editMode={true}>
            {!canManageCards && (
              <p className="text-text-700">
                <FormattedMessage defaultMessage="Kort kan inte hanteras ännu. Resenären är inte klar för korthantering." />
              </p>
            )}
            <p className="text-text-900">
              <FormattedMessage defaultMessage="Kort som nyttjas som resekort får inte samtidigt nyttjas för tjänsteresa på kort." />{' '}
              <FormattedMessage defaultMessage="Kortet måste vara helt blankt/oanvänt och kommer att kopplas permanent till denna resenärens portal-konto (eventuellt privat konto berörs aldrig)." />
            </p>
            {!isCardValid ? (
              <FormElement
                id="card_serial"
                label={<FormattedMessage defaultMessage="Kortnummer" />}
                input={
                  <Input
                    id="card_serial"
                    type="text"
                    placeholder={formatMessage({
                      defaultMessage: '1234 1234 1234 1234',
                    })}
                    value={formattedCardSerial}
                    onChange={handleCardSerialChange}
                    endAdornment={
                      isLoadingCardValidation ? (
                        <Spinner className="size-4" />
                      ) : null
                    }
                    error={cardError}
                    helperTextReserveSpace
                    disabled={!canManageCards}
                  />
                }
              />
            ) : (
              <FormElement
                label={<FormattedMessage defaultMessage="Valt kortnummer" />}
                input={
                  <div className="flex w-full justify-between">
                    {formattedCardSerial}
                    <IconCheck />
                  </div>
                }
              />
            )}
            {cardValidation?.claimable && cardAlreadyExistsOnTraveller && (
              <p className="italic text-red-700">
                <FormattedMessage defaultMessage="Kortet är giltigt men är redan kopplat till resenären i reseappen. Det går att lägga till ändå, men behövs egentligen inte." />
              </p>
            )}

            <FormElement
              id="label"
              label={<FormattedMessage defaultMessage="Namn på kort" />}
              input={
                <InputText
                  name="label"
                  control={cardFormMethods.control}
                  editMode={true}
                  maxLength={100}
                  placeholder={formatMessage({
                    defaultMessage: '(Valfritt)',
                  })}
                />
              }
            />
          </FormSection>
        </div>
        <div className="mt-4 flex w-full flex-row justify-end gap-2">
          <Button onClick={cancelEdit} variant="ghost">
            <FormattedMessage defaultMessage="Avbryt" />
          </Button>
          <Button
            startIcon={<IconCheck />}
            variant={disabled ? 'outline' : 'filled'}
            type="submit"
            disabled={disabled}
            loading={travelCardClaim.isPending && !isReassignFlow}
          >
            <FormattedMessage defaultMessage="Lägg till kort" />
          </Button>
          <Button
            startIcon={<IconCheck />}
            variant="outline"
            type="button"
            disabled={
              disabled || isLoadingTickets || ticketsToMove.length === 0
            }
            loading={isReassignFlow || (isLoadingTickets && !disabled)}
            onClick={handleAddCardAndReassignActiveTickets}
          >
            <FormattedMessage defaultMessage="Lägg till samt flytta biljetter" />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
